import { useMemo } from 'react';
import Drawer from '@app/components/Drawer';
import DataTable from '@app/components/DataTable';
import OrgModulePlaceholder from '@app/pages/org/OrgModulePlaceholder';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useConfig } from '@config/provider';
import { resolveBasePath } from '@app/utils/api';
import { friendlyTargetLabel } from '@app/utils/signals';
import type { CursorListResponse } from '@app/types/org-console';
import type { SignalScopeType, SignalWatchlistItem } from '@app/types/signals';

interface WatchlistDrawerProps {
  open: boolean;
  scopeType: SignalScopeType;
  scopeId: string;
  tier?: string;
  onClose: () => void;
}

const buildQuery = (params: Record<string, string | number | null | undefined>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined) {
      return;
    }
    const s = String(v);
    if (s.length === 0) {
      return;
    }
    sp.set(k, s);
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
};

const WatchlistDrawer = ({ open, scopeType, scopeId, tier = 'hot', onClose }: WatchlistDrawerProps) => {
  const orgId = useActiveOrgId();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgCollection } = useAuthFetch();

  const fetcher = useMemo(() => {
    if (!open || !orgId || !scopeId) {
      return null;
    }
    const qs = buildQuery({ scopeType, scopeId, tier, limit: 200 });
    return () => fetchOrgCollection<SignalWatchlistItem>(basePath, orgId, `signals/watchlist${qs}`);
  }, [basePath, fetchOrgCollection, open, orgId, scopeId, scopeType, tier]);

  const query = useApiQuery<CursorListResponse<SignalWatchlistItem>>(fetcher, [fetcher]);

  if (!open) {
    return null;
  }

  if (query.backendMissing) {
    return (
      <Drawer open={open} title="Watchlist" subtitle={`${scopeType}`} onClose={onClose} width="wide">
        <OrgModulePlaceholder intent="Implement /signals/watchlist to populate watchlist widgets." />
      </Drawer>
    );
  }

  const items = query.data?.items ?? [];

  return (
    <Drawer open={open} title={`Watchlist (${tier})`} subtitle={`${scopeType}`} onClose={onClose} width="wide">
      {query.error ? <p className="org-shell__banner">{query.error.message}</p> : null}
      {query.loading && items.length === 0 ? <p className="org-shell__banner">Loading watchlist…</p> : null}
      <DataTable
        rows={items}
        rowId={(row, idx) => row.id ?? `${row.topicKey ?? 'topic'}-${row.targetId ?? 'target'}-${idx}`}
        emptyState={<p>No watchlisted items.</p>}
        columns={[
          { key: 'topicKey', header: 'Topic', render: (row) => row.topicKey ?? '—' },
          { key: 'target', header: 'Target', render: (row) => friendlyTargetLabel(row.targetId, row.attributes ?? null) },
          {
            key: 'explanation',
            header: 'Why Selected',
            render: (row) => {
              const attrs = (row.attributes ?? {}) as Record<string, unknown>;
              const summary = typeof attrs.summary === 'string' && attrs.summary.trim().length > 0 ? attrs.summary.trim() : '—';
              const details = Array.isArray(attrs.explanation_details)
                ? attrs.explanation_details.map((entry) => (typeof entry === 'string' ? entry.trim() : '')).filter(Boolean)
                : [];
              return (
                <div>
                  <div>{summary}</div>
                  {details.length > 0 ? (
                    <div style={{ color: 'var(--color-text-muted, #6b7280)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {details.join(' • ')}
                    </div>
                  ) : null}
                </div>
              );
            },
            width: '40%'
          },
          { key: 'tier', header: 'Tier', render: (row) => row.tier ?? '—' },
          { key: 'updatedAt', header: 'Updated', render: (row) => row.updatedAt ?? '—' }
        ]}
      />
    </Drawer>
  );
};

export default WatchlistDrawer;
